import React, { useEffect, useState } from 'react'
import {
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Table,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap'
import FormCard from '../common/FormCard'
import apiFlexihoraUserService from '../../services/apiFlexihoraUserService'

const statusOptions = [
  { value: 'ACTIVE', label: 'Activo' },
  { value: 'BLOCKED', label: 'Bloqueado' },
]

function EditInstanceForm({
  formData,
  errors,
  submitted,
  isSaving,
  distributors,
  isLoadingDistributors,
  onChange,
  onSubmit,
  onCancel,
  onResetAdminPassword,
}) {
  const safeDistributors = Array.isArray(distributors) ? distributors : []
  const [users, setUsers] = useState([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [usersError, setUsersError] = useState('')
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false)

  useEffect(() => {
    let isMounted = true

    const loadUsers = async () => {
      const empresaId = formData?.flexihoraId
      if (!empresaId) {
        if (isMounted) {
          setUsers([])
          setUsersError('')
        }
        return
      }

      setIsLoadingUsers(true)
      setUsersError('')
      try {
        const data = await apiFlexihoraUserService.listByEmpresaId(empresaId)
        if (isMounted) {
          setUsers(Array.isArray(data) ? data : [])
        }
      } catch (error) {
        if (isMounted) {
          setUsers([])
          setUsersError(error?.body?.message || 'No se pudieron cargar los usuarios de Flexihora.')
        }
      } finally {
        if (isMounted) {
          setIsLoadingUsers(false)
        }
      }
    }

    loadUsers()
    return () => {
      isMounted = false
    }
  }, [formData?.flexihoraId])

  const openPasswordModal = (user) => {
    setSelectedUser(user)
    setNewPassword('')
    setPasswordError('')
    setPasswordSuccess('')
    setPasswordModalOpen(true)
  }

  const closePasswordModal = () => {
    if (isUpdatingPassword) {
      return
    }
    setPasswordModalOpen(false)
    setSelectedUser(null)
    setNewPassword('')
    setPasswordError('')
    setPasswordSuccess('')
  }

  const handleResetPassword = async () => {
    const trimmedPassword = (newPassword || '').trim()
    if (!selectedUser?.id) {
      setPasswordError('No hay usuario seleccionado.')
      return
    }
    if (!trimmedPassword) {
      setPasswordError('La contrasena es requerida.')
      return
    }

    setIsUpdatingPassword(true)
    setPasswordError('')
    setPasswordSuccess('')
    try {
      await apiFlexihoraUserService.setPassword(selectedUser.id, { password: trimmedPassword })
      setPasswordSuccess('Contrasena restablecida correctamente.')
      setTimeout(() => {
        setPasswordModalOpen(false)
      }, 600)
    } catch (error) {
      setPasswordError(error?.body?.message || 'No se pudo restablecer la contrasena.')
    } finally {
      setIsUpdatingPassword(false)
    }
  }

  return (
    <>
      <FormCard
        title={`Formulario de Instancia - ${formData.name || 'Sin nombre'}`}
        onSubmit={onSubmit}
        onCancel={onCancel}
        submitLabel="Guardar Cambios"
        showCancel
        isSubmitting={isSaving}
      >
        <FormGroup>
          <Label for="name">
            Nombre <span className="text-danger">*</span>
          </Label>
          <Input
            type="text"
            name="name"
            id="name"
            placeholder="Ingrese el nombre de la instancia"
            value={formData.name}
            disabled
          />
          {submitted && errors.name && (
            <FormFeedback>{errors.name}</FormFeedback>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="fiscalName">
            Nombre fiscal <span className="text-danger">*</span>
          </Label>
          <Input
            type="text"
            name="fiscalName"
            id="fiscalName"
            placeholder="Ingrese el nombre fiscal"
            value={formData.fiscalName}
            onChange={onChange}
            invalid={submitted && !!errors.fiscalName}
          />
          {submitted && errors.fiscalName && (
            <FormFeedback>{errors.fiscalName}</FormFeedback>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="url">URL</Label>
          <Input
            type="text"
            name="url"
            id="url"
            value={formData.url}
            readOnly
            disabled
          />
        </FormGroup>

        <FormGroup>
          <Label for="status">Estado</Label>
          <Input
            type="select"
            name="status"
            id="status"
            value={formData.status}
            onChange={onChange}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="distributorId">Distribuidor</Label>
          <Input
            type="select"
            name="distributorId"
            id="distributorId"
            value={formData.distributorId ?? ''}
            onChange={onChange}
            invalid={submitted && !!errors.distributorId}
            disabled={isLoadingDistributors}
          >
            <option value="">Sin distribuidor</option>
            {safeDistributors.map((distributor) => (
              <option key={distributor.id} value={distributor.id}>
                {distributor.name}
              </option>
            ))}
          </Input>
          {submitted && errors.distributorId && (
            <FormFeedback>{errors.distributorId}</FormFeedback>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Usuarios Flexihora</Label>
          {isLoadingUsers && <div>Cargando usuarios...</div>}
          {!isLoadingUsers && usersError && <Alert color="danger">{usersError}</Alert>}
          {!isLoadingUsers && !usersError && (
            <>
              {users.length === 0 ? (
                <div>No hay usuarios para esta empresa.</div>
              ) : (
                <Table responsive size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Estado</th>
                      <th>Restablecer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.username}</td>
                        <td>{user.enable ? 'Activo' : 'Inactivo'}</td>
                        <td>
                          <Button
                            type="button"
                            color="warning"
                            size="sm"
                            onClick={() => openPasswordModal(user)}
                          >
                            Restablecer
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </FormGroup>

      </FormCard>

      <Modal isOpen={passwordModalOpen} toggle={closePasswordModal}>
        <ModalHeader toggle={closePasswordModal}>Restablecer contrasena</ModalHeader>
        <ModalBody>
          <div className="mb-2">
            Usuario: <strong>{selectedUser?.username || '-'}</strong>
          </div>
          <FormGroup>
            <Label for="user-password">Nueva contrasena</Label>
            <Input
              id="user-password"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              disabled={isUpdatingPassword}
            />
          </FormGroup>
          {passwordError && <Alert color="danger">{passwordError}</Alert>}
          {passwordSuccess && <Alert color="success">{passwordSuccess}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="secondary" onClick={closePasswordModal} disabled={isUpdatingPassword}>
            Volver
          </Button>
          <Button type="button" color="primary" onClick={handleResetPassword} disabled={isUpdatingPassword}>
            Guardar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default EditInstanceForm
